import { FC } from 'react';

import { Badge, CheckOutline, ChevronDownOutline, HStack, Menu, MenuButton, MenuItem, MenuList, Portal, StyleProps } from '@bonusly/design-system';

import { GoalStatus } from '../../../../types/goal';

interface GoalStatusBadgeProps extends StyleProps {
	status: GoalStatus;
	size?: 'sm' | 'md' | 'lg';
	toggleable?: boolean;
	onStatusChange?: (newStatus: GoalStatus) => void;
}

export const getStatusStyles = (status: GoalStatus) => {
	switch (status) {
		case 'on_track':
			return { bg: 'primary.bgAlpha', color: 'primary.fgStrong', borderColor: 'border.soft' };
		case 'off_track':
			return { bg: 'error.bg', color: 'error.text', borderColor: 'border.soft' };
		case 'at_risk':
			return { bg: 'warning.bg', color: 'warning.text', borderColor: 'border.soft' };
		case 'completed':
			return { bg: 'success.bg', color: 'success.text', borderColor: 'border.soft' };
		case 'not_started':
		default:
			return { bg: 'secondary.bgAlpha', color: 'text.soft', borderColor: 'border.soft' };
	}
};

export const getStatusLabel = (status: GoalStatus) => {
	const statusLabels: Record<GoalStatus, string> = {
		on_track: 'On track',
		off_track: 'Off track',
		at_risk: 'At risk',
		not_started: 'Not started',
		completed: 'Completed',
	};
	return statusLabels[status];
};

export const GoalStatusBadge: FC<GoalStatusBadgeProps> = ({ status, size = 'md', toggleable = false, onStatusChange, ...rest }) => {
	const statusStyle = getStatusStyles(status);
	const label = getStatusLabel(status);

	const sizeStyles = {
		sm: { px: 3, h: '32px', fontSize: 'sm' }, // 12px padding, 32px height, sm text
		md: { px: 3, h: 8, py: 1.5, fontSize: 'sm' },
		lg: { px: 4, py: 2, fontSize: 'sm' },
	};

	const baseStyles = {
		...statusStyle,
		...sizeStyles[size],
		borderRadius: 'full',
		fontWeight: 'medium',
		textTransform: 'none' as const,
		...rest,
	};

	const handleStatusSelect = (newStatus: GoalStatus) => {
		if (newStatus !== status) {
			onStatusChange?.(newStatus);
		}
	};

	const renderStatusMenuItem = (statusOption: GoalStatus) => {
		const isSelected = statusOption === status;
		const statusStyle = getStatusStyles(statusOption);
		const statusLabel = getStatusLabel(statusOption);

		return (
			<MenuItem
				key={statusOption}
				onClick={(e) => {
					e.stopPropagation();
					handleStatusSelect(statusOption);
				}}
				p={2}
				borderRadius="md"
			>
				<HStack justify="space-between" align="center" w="full">
					<Badge {...statusStyle} px={3} py={1} borderRadius="full" fontSize="sm" fontWeight="medium" textTransform="none">
						{statusLabel}
					</Badge>
					{isSelected && <CheckOutline size="sm" color="primary.fg" />}
				</HStack>
			</MenuItem>
		);
	};

	if (toggleable) {
		return (
			<Menu>
				<MenuButton onClick={(e) => e.stopPropagation()}>
					<Badge
						{...baseStyles}
						cursor="pointer"
						transition="all 0.2s"
						_hover={{
							transform: 'scale(1.02)',
							boxShadow: 'sm',
						}}
						display="flex"
						alignItems="center"
						gap={1}
					>
						<HStack spacing={1} align="center">
							<span>{label}</span>
							<ChevronDownOutline size="xs" />
						</HStack>
					</Badge>
				</MenuButton>
				<Portal>
					<MenuList>
						{renderStatusMenuItem('not_started')}
						{renderStatusMenuItem('on_track')}
						{renderStatusMenuItem('at_risk')}
						{renderStatusMenuItem('off_track')}
						{renderStatusMenuItem('completed')}
					</MenuList>
				</Portal>
			</Menu>
		);
	}

	return <Badge {...baseStyles}>{label}</Badge>;
};
